import { Input } from 'antd';
import UploadProgress from '~/shared/components/form-fields/upload-progress/upload-progress';
import compact from 'lodash/compact';
import { Line as ProgressBar } from 'rc-progress';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';

import Field from '~/shared/components/field';
import FlashMessage from '~/shared/components/flash-message';
import ErrorMessage from '~/shared/components/form-fields/error-message';
import FileField from '~/shared/components/form-fields/file-field';
import FilesUploaderPlaceholder from '~/shared/components/form-fields/files-uploader-field/components/files-uploader-placeholder';
import Label from '~/shared/components/form-fields/label/label';
import {
  FileInteractionIconSelector,
  getFileInteractionIconCopy,
} from '~/shared/components/file-interaction-icon/file-interaction-icon';
import { checkFileType, splitFileName } from '~/shared/helpers/file-helpers';
import { useTypedTranslation } from '~/shared/hooks';
import { useAppDispatch, useAppSelector } from '~/shared/store/hooks';
import { changeStateOfConfirmModal } from '~/shared/store/slices/app/app-slice';
import { getAppLanguage } from '~/shared/store/slices/app/selectors';

import './files-uploader-field.scss';

type FilesUploaderFieldPropsType = {
  change;
  withFileType;
  meta;
  fields;
};

export const MAX_NUMBER_OF_FILES = 10;
export const MAX_FILE_SIZE = 80 * 1024 * 1024;
export const MAX_BATCH_FILE_SIZE = 100 * 1024 * 1024;

const FilesUploaderField = (props: FilesUploaderFieldPropsType) => {
  const { withFileType, setValue, meta, value, config, fields, onChange, name } = props;
  const t = useTypedTranslation(['message', 'text', 'error']);
  const lng = useAppSelector(getAppLanguage);
  const interactionIconCopy = getFileInteractionIconCopy(lng);
  const maxNumberOfFiles = config?.maxNumberOfFiles ?? MAX_NUMBER_OF_FILES;
  const maxFileSize = config?.maxFileSize ?? MAX_FILE_SIZE;
  const maxBatchFileSize = config?.maxBatchFileSize ?? MAX_BATCH_FILE_SIZE;

  const showFileRejection = (fileRejections = []) => {
    const hasTooLarge = fileRejections.some((rej) => rej.errors?.some((e) => e.code === 'file-too-large'));
    const hasInvalidType = fileRejections.some((rej) => rej.errors?.some((e) => e.code === 'file-invalid-type'));

    if (hasTooLarge) {
      toast.error(
        <FlashMessage
          title={t('message.max-size-of-file-limit.title')}
          message={t('message.max-size-of-file-limit.message')}
        />,
      );
      return;
    }

    if (hasInvalidType) {
      toast.error(
        <FlashMessage
          title={t('message.invalid-file-format.title')}
          message={t('message.invalid-file-format.message')}
        />,
      );
      return;
    }

    toast.error(<FlashMessage title={t('message.file-rejected.title')} message={t('message.file-rejected.message')} />);
  };

  const handleDropFile = async (acceptedFiles) => {
    if (!acceptedFiles?.length) {
      showFileRejection();
      return;
    }

    let totalSize = 0;
    let isMoreThanMaxFile = false;
    let hasUnsupportedFormatInAccepted = false;

    const mapped = acceptedFiles.map((file) => {
      totalSize += file?.size || 0;
      if (file?.size >= maxFileSize) isMoreThanMaxFile = true;

      return new Promise((resolve) => {
        if (!checkFileType(file, ['any'])) {
          hasUnsupportedFormatInAccepted = true;
          return resolve(null);
        }

        resolve({
          path: file,
          status: 'new',
          name: splitFileName(file?.name)?.[1],
        });
      });
    });

    if (totalSize > maxBatchFileSize) {
      toast.error(
        <FlashMessage
          title={t('message.max-size-of-files-limit.title')}
          message={t('message.max-size-of-files-limit.message')}
        />,
      );
      return;
    }

    if (isMoreThanMaxFile) {
      toast.error(
        <FlashMessage
          title={t('message.max-size-of-file-limit.title')}
          message={t('message.max-size-of-file-limit.message')}
        />,
      );
      return;
    }

    if (hasUnsupportedFormatInAccepted) {
      toast.error(
        <FlashMessage
          title={t('message.invalid-file-format.title')}
          message={t('message.invalid-file-format.message')}
        />,
      );
      return;
    }

    const files = compact(await Promise.all(mapped));
    onChange(files);
  };

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: maxNumberOfFiles,
    maxSize: maxFileSize,
    onDropAccepted: handleDropFile,
    onDropRejected: showFileRejection,
    accept: config?.filesFormat,
  });
  const rootProps = getRootProps();
  const inputProps = getInputProps();
  // const rootProps = getRootProps({ refKey: 'innerRef' });
  const formats = Object.keys(config?.filesFormat ?? {});
  const dispatch = useAppDispatch();

  return (
    <>
      {!config?.isViewMode && (
        <FilesUploaderPlaceholder
          maxBatchFilesSize={`${Math.ceil(maxBatchFileSize / (1024 * 1024))}MB`}
          maxFileSize={`${Math.ceil(maxFileSize / (1024 * 1024))}MB`}
          maxNumberOfFiles={maxNumberOfFiles}
          rootProps={rootProps}
          t={t}
          inputProps={inputProps}
          formats={formats}
        />
      )}
      {value && value.length > 0 && (
        <div>
          {value.map((data, fieldIndex) => (
            <div key={data?._id ?? data?.path?.path ?? data?.path?.name ?? `${name}-${fieldIndex}`}>
              <div className="files-uploader-field__content">
                {config?.withFileName && (
                  <div className="files-uploader-field__file-input">
                    <div className="files-uploader-field__file-input-field">
                      <Label htmlFor={`${name}[${fieldIndex}].name`}>
                        {t('field.file-name.title', {
                          ns: ['field'],
                        })}
                      </Label>
                      <Input
                        onChange={(e) => {
                          setValue(`${name}.${fieldIndex}.name`, e.target.value);
                        }}
                        defaultValue={data?.name}
                        name={`${name}.${fieldIndex}.name`}
                        type="text"
                      />
                    </div>
                    <div className="files-uploader-field__file-input-field files-uploader-field__description-input">
                      <Label htmlFor={`${name}[${fieldIndex}].description`}>Опис файлу</Label>
                      <Input
                        maxLength={34}
                        onChange={(e) => {
                          setValue(`${name}.${fieldIndex}.description`, e.target.value.slice(0, 34));
                        }}
                        defaultValue={data?.description}
                        name={`${name}.${fieldIndex}.description`}
                        type="text"
                      />
                    </div>
                    {config?.withInteractionIcon && (
                      <div className="files-uploader-field__file-input-field files-uploader-field__interaction-icon-input">
                        <Label htmlFor={`${name}[${fieldIndex}].interactionIcon`}>{interactionIconCopy.label}</Label>
                        <FileInteractionIconSelector
                          lng={lng}
                          value={data?.interactionIcon ?? null}
                          onChange={(nextValue) => {
                            setValue(`${name}.${fieldIndex}.interactionIcon`, nextValue, {
                              shouldDirty: true,
                              shouldTouch: true,
                            });
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
                <FileField
                  onDelete={() =>
                    dispatch(
                      changeStateOfConfirmModal({
                        onTrigger: async (itemKey) => {
                          setValue(
                            `${name}`,
                            value?.filter((item, index) => {
                              return index !== fieldIndex;
                            }),
                          );
                        },
                        title: t('modal.removal-of-file.title'),
                        message: t('modal.removal-of-file.message'),
                        isShowed: true,
                        isRemoving: true,
                        wrapClassName:
                          'shared-confirm-action-modal shared-confirm-action-modal--danger files-manager-confirm-action-modal',
                        //  itemKey: props?.itemKey,
                      }),
                    )
                  }
                  withFileName={config?.withFileName}
                  fieldIndex={fieldIndex}
                  withDeleteButton={config?.withDeleteButton && !config?.isViewMode}
                  data={data}
                />
              </div>
              <UploadProgress file={data?.path} lng={lng} />
              {withFileType && (
                <>
                  <Label htmlFor={`${name}[${fieldIndex}].name`}>
                    {t('field.file-type.title', {
                      ns: ['field'],
                    })}
                  </Label>
                  <Field name={`${name}[${fieldIndex}].type`} type="text" component={Input} />
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {/*    {config?.uploadingProgress !== null && config?.uploadingProgress !== undefined && (
        <ProgressBar percent={config?.uploadingProgress * 100} strokeWidth={2} trailWidth={2} strokeColor="aqua" />
      )}*/}
      {meta?.touched && meta?.error && <ErrorMessage error={meta?.error} />}
    </>
  );
};

export default FilesUploaderField;
